import React, { useState, useEffect, useCallback } from "react";
import { BellRing, X } from "lucide-react";

// ── First-run "enable desktop alerts" banner ───────────────────────────────
// A non-blocking, one-time card shown after rollout when we detect that this
// browser has NOT yet been asked about notifications (permission === "default")
// and the user hasn't dismissed it before. Deliberately NOT a modal: a blocking
// prompt that auto-fires would risk browser anti-abuse blocking and accidental
// permanent "denied". The Enable click supplies the user gesture the native
// prompt requires.
//
// Dismissal (Enable OR Not now OR ✕) is remembered forever via a VERSIONED
// localStorage key — same idiom as RELEASE_NOTES_KEY — so it never nags again.
// Bump the version suffix only if we ever want to re-surface it intentionally.
//
// Safe on every browser: if the Notification API is missing, the banner simply
// never renders (returns null).

const NOTIF_SUPPORTED = typeof window !== "undefined" && "Notification" in window;
const DISMISS_KEY = "rocklink.mentionNotifPrompt.v1";

function alreadyDismissed() {
  try { return window.localStorage.getItem(DISMISS_KEY) === "1"; }
  catch { return false; }
}
function persistDismiss() {
  try { window.localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
}

export default function MentionNotifBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Show only when: supported, never asked (default), and not dismissed before.
    if (!NOTIF_SUPPORTED) return;
    let perm = "default";
    try { perm = window.Notification.permission || "default"; } catch { return; }
    if (perm === "default" && !alreadyDismissed()) setShow(true);
  }, []);

  const dismiss = useCallback(() => {
    persistDismiss();
    setShow(false);
  }, []);

  const enable = useCallback(async () => {
    // Requesting from this click satisfies the required user gesture. Whatever
    // the user chooses (grant/deny/close), we've now asked — don't nag again.
    try { await window.Notification.requestPermission(); } catch { /* noop */ }
    // Let the mentions inbox pick up the new permission on next focus/poll.
    try { window.dispatchEvent(new Event("focus")); } catch { /* noop */ }
    dismiss();
  }, [dismiss]);

  if (!show) return null;

  return (
    <div
      className="fixed z-[60] bottom-4 right-4 max-w-[340px] w-[calc(100vw-2rem)] sm:w-[340px] rl-frost border border-[hsl(var(--border))] rounded-xl shadow-lg p-3.5 animate-in fade-in slide-in-from-bottom-2 duration-300 motion-reduce:animate-none"
      role="dialog"
      aria-label="Enable desktop alerts for mentions"
      data-testid="mention-notif-banner"
    >
      <button
        type="button"
        onClick={dismiss}
        className="absolute top-2 right-2 w-6 h-6 inline-flex items-center justify-center rounded-full text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/60 transition-colors"
        aria-label="Dismiss"
        data-testid="mention-notif-banner-dismiss"
      >
        <X className="w-3.5 h-3.5" strokeWidth={2.2} />
      </button>

      <div className="flex items-start gap-2.5 pr-5">
        <span className="mt-0.5 inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-500/15 shrink-0">
          <BellRing className="w-4 h-4 text-amber-500" strokeWidth={2.2} />
        </span>
        <div className="min-w-0">
          <div className="text-[13px] font-semibold text-[hsl(var(--foreground))] leading-tight">
            Get pinged when you're tagged
          </div>
          <p className="text-[11.5px] text-[hsl(var(--muted-foreground))] leading-snug mt-1">
            Turn on desktop alerts so a mention reaches you even when RockLink
            isn't the window in front. You can change this anytime from the
            <span className="whitespace-nowrap"> @ menu</span>.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 mt-3">
        <button
          type="button"
          onClick={dismiss}
          className="text-[12px] font-medium text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] px-2.5 py-1.5 rounded-full transition-colors"
          data-testid="mention-notif-banner-later"
        >
          Not now
        </button>
        <button
          type="button"
          onClick={enable}
          className="rl-lit-btn text-[12px] font-semibold text-white bg-amber-500 hover:bg-amber-600 px-3.5 py-1.5 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/50"
          data-testid="mention-notif-banner-enable"
        >
          Enable alerts
        </button>
      </div>
    </div>
  );
}
